import React from 'react'
import styled from '@emotion/styled'
import { Locale, Translation } from 'locale'
import { CollectionType, DealSettlement, NegotiationInstruction, PreparedOrderClearingDetail, SettlementVenue, ValidationIssue } from 'api'
import { CollapsePanel } from 'view/components/panels'
import { DialogField, DialogRow } from 'view/components/inputs'
import { CollectionTypeSelectboxField, RFQSelectboxField } from 'view/shared/entity'
import { isAonOrOcoCollection, Severity, ValidationType } from 'model'
import { ClearingSection } from './ClearingSection'
import { Nullable } from 'utils'

interface RFQDetailsSectionProps {
  readOnly: boolean
  durationMinutes: number
  settlementVenue: Nullable<SettlementVenue>
  settlementVenueOptions: string[]
  clearingHouseId: string | null
  sponsorId: string | null
  csd: DealSettlement | null
  preparedOrderClearingDetails: PreparedOrderClearingDetail[]
  preparedCollectionType: CollectionType | null
  settlementVenueReadOnly: boolean
  allOrdersAreUs: boolean
  onChangeNegotiationDetails: (partialNegotiationInstruction: Partial<NegotiationInstruction>) => unknown
  onChangeCollectionType: (collection: CollectionType) => unknown
}

const ContentWrapper = styled.div`
  width: 100%;
  padding: 20px 12px;

  & > ${DialogRow}:not(:last-child) {
    margin-bottom: 19px;
  }
`

export const RFQDetailsSection: React.FunctionComponent<RFQDetailsSectionProps> = props => {
  const {
    readOnly,
    durationMinutes,
    settlementVenue,
    settlementVenueOptions,
    clearingHouseId,
    sponsorId,
    csd,
    preparedOrderClearingDetails,
    preparedCollectionType,
    settlementVenueReadOnly,
    allOrdersAreUs,
    onChangeNegotiationDetails,
    onChangeCollectionType
  } = props

  const subtitle = Locale.translate(Translation.SC_DETAILS_SUMMARY, {
    mins: durationMinutes
  })

  const durationValidationIssues: ValidationIssue[] =
    durationMinutes === 0 || durationMinutes >= 1440 // 1 day
      ? [
          {
            errorMessage: Locale.translate(Translation.SC_INVALID_VALUE),
            fieldName: '',
            severity: Severity.ERROR,
            type: ValidationType.INVALID_VALUE
          }
        ]
      : []

  const onChangeDuration = (rfqDurationMins: number) => {
    onChangeNegotiationDetails({ rfqDurationMins })
  }

  return (
    <CollapsePanel
      dataTestId={'SendRFQPanel-Details'}
      title={Locale.translate(Translation.TC_DETAILS)}
      subtitle={subtitle}
      defaultCollapsed={false}>
      <ContentWrapper>
        <DialogRow>
          <DialogField>
            <RFQSelectboxField
              validationIssues={durationValidationIssues}
              label={Locale.translate(Translation.TC_DURATION_HOURS_MINS)}
              value={durationMinutes}
              readOnly={readOnly}
              onUpdate={onChangeDuration}
            />
          </DialogField>
          <DialogField />
          <DialogField />
        </DialogRow>
        <ClearingSection
          readOnly={readOnly}
          settlementVenue={settlementVenue}
          settlementVenueOptions={settlementVenueOptions}
          clearingHouseId={clearingHouseId}
          sponsorId={sponsorId}
          csd={csd}
          preparedOrderClearingDetails={preparedOrderClearingDetails}
          settlementVenueReadOnly={settlementVenueReadOnly}
          allOrdersAreUs={allOrdersAreUs}
          onChangeNegotiationDetails={onChangeNegotiationDetails}
        />
        {preparedCollectionType && (
          <DialogRow>
            <DialogField>
              <CollectionTypeSelectboxField
                label={Locale.translate(Translation.TC_COLLECTION_TYPE)}
                value={preparedCollectionType}
                readOnly={readOnly || !isAonOrOcoCollection(preparedCollectionType)}
                onUpdate={onChangeCollectionType}
                enabledCollectionTypes={[CollectionType.ONE_CANCELS_OTHERS, CollectionType.ALL_OR_NOTHING]}
              />
            </DialogField>
            <DialogField />
            <DialogField />
          </DialogRow>
        )}
      </ContentWrapper>
    </CollapsePanel>
  )
}
